// Nederlandse teksten voor Klantberichten. Dezelfde sleutels als messages.en.ts;
// de typecheck faalt als er een ontbreekt of bijkomt.
//
// Let op: nergens "verstuurd" of "ontvangen" waar het Engels het niet ook zegt.
// Goedkeuren is een besluit, geen bezorging.

import type { Message, Vars } from './en.ts';
import type { messagesEn } from './messages.en.ts';

export const messagesNl: Readonly<Record<keyof typeof messagesEn, Message>> = {
  // --- het scherm -------------------------------------------------------------
  'msg.title': 'Klantberichten',
  'msg.intro': 'Vraag klanten om ontbrekende documenten.',
  'msg.noTransport': 'E-mails worden nog niet verstuurd. Goedgekeurde berichten worden bewaard.',
  'msg.empty.title': 'Nog geen berichten',
  'msg.empty.line': 'Open een banktransactie zonder factuur om de klant iets te vragen',
  'msg.empty.action': 'Naar Bankafletteren',
  'msg.nothingToAsk': 'Er is niets om naar te vragen.',
  'msg.created': 'Het verzoek staat open. Schrijf het bericht en keur het daarna goed — er gaat niets weg voordat je dat doet.',
  'msg.createdExisting': 'Deze klant is daar al naar gevraagd. Het bestaande verzoek is geopend in plaats van een tweede.',
  'msg.selectAdministration': 'Kies eerst een administratie.',
  'msg.contactAdded': 'Vastgelegd, en niet geverifieerd. Er kan niets naartoe worden gestuurd totdat iemand bevestigt dat het adres klopt.',
  'msg.contactVerified': 'Geverifieerd, met jouw uitleg van hoe, voor precies dat adres.',

  // --- de lijst ---------------------------------------------------------------
  'msg.list.title': 'Verzoeken',
  'msg.list.sub': 'Alles waar dit kantoor om heeft gevraagd, nieuwste eerst',
  'msg.list.reading': 'Verzoeken lezen…',
  'msg.noSubject': 'Nog geen bericht geschreven',
  'msg.addressUnverified': ' · adres niet geverifieerd',
  'msg.select.title': 'Kies een verzoek',
  'msg.select.text': 'Het bericht, aan wie het gericht is en alles wat terugkwam, verschijnen hier.',

  // --- een nieuw verzoek ------------------------------------------------------
  'msg.new.title': 'Hiernaar vragen',
  'msg.new.bankLine': 'Banktransactie',
  'msg.new.document': 'Document',
  'msg.new.roleCannot': (v: Vars) => `Jouw rol (${v['role']}) mag geen klantverzoek aanmaken.`,
  'msg.new.noContacts':
    'Voor deze administratie is nog niemand vastgelegd om aan te schrijven. Voeg hieronder een contactpersoon toe en kom dan terug — een verzoek moet gericht zijn aan iemand die het kantoor heeft vastgelegd.',
  'msg.new.ask': 'Vragen aan',
  'msg.new.choose': 'Kies een contactpersoon…',
  'msg.new.notVerified': ' — niet geverifieerd',
  'msg.new.unverifiedNote':
    'Je kunt nu al schrijven aan een niet-geverifieerde contactpersoon en later goedkeuren. Voor versturen moet iemand eerst het adres hebben bevestigd.',
  'msg.new.notNow': 'Niet nu',
  'msg.new.open': 'Verzoek openen',

  // --- één verzoek ------------------------------------------------------------
  'msg.detail.reading': 'Dit verzoek lezen…',
  'msg.detail.to': (v: Vars) => `Aan ${v['name']}`,
  'msg.step.written': 'Geschreven',
  'msg.step.approved': 'Goedgekeurd',
  'msg.step.sending': 'Versturen',
  'msg.step.sent': 'Verstuurd',
  'msg.detail.unverified':
    'Niemand heeft dit adres bevestigd. Je kunt erheen schrijven, maar niet versturen — bevestig het hieronder bij Contactpersonen, dan kan het verzoek worden goedgekeurd.',
  'msg.detail.addressMoved':
    'De contactpersoon is aangepast nadat dit bericht werd geschreven, dus de goedkeuring zou gelden voor een adres dat niet meer actueel is. Schrijf het bericht opnieuw om het nieuwe adres te gebruiken.',
  'msg.detail.operation': 'Verzendopdracht:',
  'msg.saved': 'Opgeslagen als nieuwe versie. Er is niets goedgekeurd en niets verstuurd.',
  'msg.writeFirst': 'Schrijf eerst het bericht.',
  'msg.approved': (v: Vars) =>
    `Goedgekeurd en in de wachtrij gezet als opdracht ${v['id']}. Volg de status hierboven — in deze omgeving meldt die dat er niets is verstuurd.`,
  'msg.attached':
    'Gekoppeld. Het verzoek wacht tot iemand controleert of dit echt het gevraagde document is — er is niets geboekt.',
  'msg.replies.title': 'Wat terugkwam',
  'msg.replies.noNote': 'Gekoppeld zonder notitie',
  'msg.replies.noProposal': ' · er is nog niets uit voorbereid',
  'msg.replies.proposal': (v: Vars) => ` · een voorstel is ${v['status']}`,
  'msg.replies.prepared': 'voorbereid',
  'msg.replies.open': 'Document openen',
  'msg.events.title': 'E-mailgebeurtenissen',
  'msg.events.thread': (v: Vars) => ` · gesprek ${v['ref']}`,
  'msg.revisions.title': 'Alle versies van dit bericht',
  'msg.revisions.none': 'Er is nog niets geschreven.',
  'msg.revisions.subject': 'Onderwerp',
  'msg.revisions.to': 'Aan',
  'msg.revisions.written': 'Geschreven',
  'msg.revisions.approved': 'Goedgekeurd',
  'msg.revisions.note':
    'Oudere versies worden bewaard, niet vervangen. Een goedkeuring hoort bij één ervan, zodat “wat hebben we precies goedgekeurd, en naar welk adres?” te beantwoorden blijft.',

  // --- waarom goedkeuren niet kan ---------------------------------------------
  'msg.blocked.role': (v: Vars) => `Jouw rol (${v['role']}) mag geen bericht goedkeuren voor verzending.`,
  'msg.blocked.unverified': 'Niemand heeft dit adres bevestigd. Verifieer eerst de contactpersoon hieronder.',
  'msg.blocked.moved': 'De contactpersoon is aangepast nadat dit bericht werd geschreven. Schrijf het opnieuw.',

  // --- het bericht ------------------------------------------------------------
  'msg.draft.roleCannot': (v: Vars) => `Jouw rol (${v['role']}) mag dit verzoek lezen, maar het bericht niet schrijven of goedkeuren.`,
  'msg.draft.title': 'Het bericht',
  'msg.draft.subject': 'Onderwerp',
  'msg.draft.body': 'Bericht',
  'msg.draft.goesTo': (v: Vars) =>
    `Gaat naar ${v['address']}, het adres dat voor deze contactpersoon is vastgelegd. Wil je het wijzigen, pas dan de contactpersoon aan — die is daarna weer niet geverifieerd.`,
  'msg.draft.save': 'Deze versie opslaan',
  'msg.draft.approve': 'Goedkeuren en verzending klaarzetten',
  'msg.draft.approveTitle': 'Precies dit bericht goedkeuren en de verzending klaarzetten',

  // --- een antwoord -----------------------------------------------------------
  'msg.reply.nothing': 'Er is nog niets gevraagd, dus er is niets waar een antwoord op kan komen.',
  'msg.reply.title': 'Koppel wat terugkwam',
  'msg.reply.note':
    'Het document moet al binnen zijn, via de binnenkomst van deze administratie. Koppelen legt vast dat iemand denkt dat het dit verzoek beantwoordt; het keurt geen boeking goed en bewijst niet dat het juiste document binnen is.',
  'msg.reply.document': 'Document',
  'msg.reply.documentPlaceholder': 'Het id van de binnenkomst',
  'msg.reply.thread': 'E-mailgesprek (optioneel)',
  'msg.reply.why': 'Waarom je denkt dat dit het antwoord is',
  'msg.reply.attach': 'Koppelen',

  // --- contactpersonen --------------------------------------------------------
  'msg.contacts.title': 'Contactpersonen',
  'msg.contacts.pick': 'Administratie',
  'msg.contacts.choose': 'Kies een administratie',
  'msg.contacts.pickLine': 'Kies een administratie om de contactpersonen te zien.',
  'msg.contacts.reading': 'Contactpersonen lezen…',
  'msg.contacts.none': 'Nog niemand vastgelegd voor deze administratie.',
  'msg.contacts.version': (v: Vars) => `versie ${v['version']}`,
  'msg.contacts.verifiedHow': (v: Vars) => `geverifieerd — ${v['method']}`,
  'msg.contacts.noAccount': 'geen uitleg gegeven',
  'msg.contacts.notVerifiedLower': 'niet geverifieerd',
  'msg.contacts.howLabel': 'Hoe heb je dit adres bevestigd?',
  'msg.contacts.howPlaceholder': 'Het nummer op de factuur gebeld',
  'msg.contacts.record': 'Verificatie vastleggen',
  'msg.contacts.verified': 'Geverifieerd',
  'msg.contacts.notVerified': 'Niet geverifieerd',
  'msg.contacts.verify': 'Verifiëren',
  'msg.contacts.close': 'Sluiten',
  'msg.contacts.addTitle': 'Contactpersoon vastleggen',
  'msg.contacts.address': 'E-mailadres',
  'msg.contacts.name': 'Naam (optioneel)',
  'msg.contacts.addNote':
    'Een nieuwe contactpersoon is nooit geverifieerd. Iemand moet bevestigen dat het adres klopt voordat er iets naartoe kan, en dat is een aparte handeling met een eigen vastlegging.',
  'msg.contacts.add': 'Vastleggen',
  'msg.contacts.roleCannot': (v: Vars) => `Jouw rol (${v['role']}) mag deze contactpersonen lezen, maar niet wijzigen.`,

  // --- fouten -----------------------------------------------------------------
  'msg.error.read': 'Dit kon niet worden gelezen.',
  'msg.error.transport': 'De dienst was niet bereikbaar.',
  'msg.error.absent': 'Dit is niet beschikbaar in jouw sessie.',
  'msg.error.forbiddenRead': 'Jouw rol mag dit niet lezen.',
  'msg.error.action': 'Het verzoek kon niet worden afgerond.',
  'msg.error.stale': 'Dit is gewijzigd sinds het scherm werd geladen, dus er is niets vastgelegd. Laad opnieuw en kijk wat er nu staat.',
  'msg.error.forbidden': 'Jouw rol mag dit niet doen.',
  'msg.error.gone': 'Dat is er niet meer — misschien heeft iemand het al afgehandeld.',
  'msg.error.unverified': 'Er is niets verstuurd: niemand heeft dat adres bevestigd. Verifieer de contactpersoon en keur opnieuw goed.',
  'msg.error.refused': (v: Vars) => `Het verzoek is geweigerd: ${v['fields']}.`,
  'msg.error.validation': 'controle mislukt',
};
